// Product Management Types
import {
  DcmFunnelStageMaster,
  DcmSegmentMaster,
  DcmSource,
  DcmWho,
  Segment,
} from './knowledge';

export interface ProductCategory {
  id: number;
  name: string;
  slug: string;
  description?: string;
  created_at: string;
  updated_at: string;
  pivot?: {
    product_id: number;
    product_category_id: number;
  };
}

export interface ProductTag {
  id: number;
  name: string;
  slug: string;
  created_at: string;
  updated_at: string;
  pivot?: {
    product_id: number;
    tag_id: number;
  };
}

export interface ProductImage {
  id: string;
  url: string;
  alt: string;
  is_primary: boolean;
  order: number;
}

export interface NotificationRecipient {
  id: number;
  name: string;
  email: string;
  role: string;
  is_default: boolean;
}

export interface ProductDimensions {
  length: number | null;
  width: number | null;
  height: number | null;
  unit: 'cm' | 'm' | 'in' | 'ft';
}

export interface ProductMetadata {
  brand?: string;
  manufacturer?: string;
  model?: string;
  weight?: string;
  dimensions?: ProductDimensions;
  custom_fields?: Record<string, any>;
}

// Extended metadata types for product marketing information
export interface ProductInfo {
  name?: string;
  brand?: string;
  category?: string;
  market_type?: string;
  sales_model?: string;
  tagline?: string;
  description?: string;
  usp?: string;
  development_structure?: string;
  use_scenarios?: string;
  variations?: string;
}

export interface PricingOffer {
  main_cta?: string;
  price_visibility?: string;
  price_details?: string;
  offers_campaigns?: string;
}

export interface TargetInsights {
  pain_emotion?: string;
  ideal_state?: string;
  barriers?: string;
  purchase_factor?: string;
}

export interface Target {
  segment?: string;
  insights?: TargetInsights;
}

export interface Benefits {
  functional?: string;
  economic?: string;
  emotional?: string;
  social?: string;
  self_realization?: string;
  summary?: string;
}

export interface TrustProof {
  statement?: string;
  source?: string;
  url?: string;
}

export interface Positioning {
  /** ②: 「◯◯なら、{商品名}」の「◯◯」に入るポジショニング文（USPと商品内容を統合した専用フィールド） */
  statement_lead?: string;
  pop?: string[];
  pod?: string[];
  trust_proof?: TrustProof[];
}

export interface Appeal {
  recommended_types?: string[];
  direction?: string;
  winning_copy?: string;
  structure_pattern?: string;
}

export interface BrandGuideline {
  color_palette?: {
    primary?: string;
    secondary?: string;
    accent?: string;
    background?: string;
    text?: string;
  };
  logo?: {
    url?: string;
    type?: string;
  };
  typography?: {
    heading_style?: string;
    body_style?: string;
    font_suggestion?: string;
  };
  tone_and_manner?: string;
  design_style?: string;
  visual_guide?: {
    image_usage?: string;
    layout_tendency?: string;
  };
  regulation?: {
    /** NGワード / 禁止表現 */
    ng_words?: string[];
    /** 必須表記（商標、薬事法、特商法などの固定文言） */
    required_notices?: string[];
    /** コンプライアンス備考（法規制、社内レギュレーション等の自由記述） */
    compliance_notes?: string;
    /** LP分類（例: EC、SaaS、美容、金融 ほか） */
    lp_category?: string;
  };
}

export interface FetchProgress {
  basic_info: 'pending' | 'completed' | 'failed';
  brand_guideline: 'pending' | 'completed' | 'failed';
  images: 'pending' | 'completed' | 'completed_with_warning' | 'failed';
  competitors: 'pending' | 'completed' | 'failed';
}

export interface ExtractionSource {
  url: string;
  title?: string;
}

export interface ExtractionFieldMeta {
  source_url?: string;
  source_title?: string;
  confidence?: number;
  basis?:
    | 'html'
    | 'ocr'
    | 'web_search'
    | 'schema_org'
    | 'meta_tags'
    | 'user_input'
    | 'inferred';
}

export interface ExtractionMeta {
  method?: string;
  extracted_at?: string;
  primary_url?: string;
  sources?: ExtractionSource[];
  field_confidence?: Record<string, ExtractionFieldMeta>;
  warnings?: string[];
}

export interface ProductExtendedMetadata {
  product?: ProductInfo;
  pricing_offer?: PricingOffer;
  segments?: Segment[]; // 複数セグメント対応
  segment_master?: DcmSegmentMaster[];
  funnel_stage_master?: DcmFunnelStageMaster[];
  who?: DcmWho[];
  source?: DcmSource[];
  positioning?: Positioning;
  brand_guideline?: BrandGuideline;
  fetch_progress?: FetchProgress;
  extraction_meta?: ExtractionMeta;
  onboarding_assets?: {
    logo_image_id?: number | null;
    product_image_id?: number | null;
  };
  // 後方互換性のため残す（deprecated - segmentsに移行）
  target?: Target;
  benefits?: Benefits;
  appeal?: Appeal;
}

export interface Product {
  id: number;
  organization_id: number;
  name: string;
  url?: string; // URL登録用
  category?: string; // 後方互換性のため保持、但しoptional
  description: string;
  status:
    | 'active'
    | 'inactive'
    | 'discontinued'
    | 'fetching'
    | 'failed'
    | number; // fetchingとfailedを追加
  price: number | null;
  sale_price: number | null;
  currency: string;
  stock_quantity: number;
  manage_stock: boolean;
  images: ProductImage[] | null;
  tags?: string[]; // 後方互換性のため保持、但しoptional
  metadata: ProductMetadata;
  extended_metadata?: ProductExtendedMetadata;
  created_by: number | null;
  updated_by: number | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;

  // リレーション - APIからロードされる
  categories?: ProductCategory[];
  tags_relation?: ProductTag[]; // 'tags'と区別するためにtags_relationとする
  organization?: any; // 必要に応じて組織の型を定義
  creator?: any;
  updater?: any;
  product_images?: ProductImage[]; // Knowledge\ProductImageテーブルからロードされる画像

  // Computed properties from backend
  primary_image?: ProductImage | null;
  formatted_price?: string;
  formatted_sale_price?: string | null;
  is_on_sale?: boolean;
  discount_percentage?: number;
}

export interface CreateProductDto {
  name: string;
  category: string;
  description?: string;
  status: 'active' | 'inactive' | 'discontinued';
  price: number;
  sale_price?: number | null;
  currency?: string;
  stock_quantity?: number;
  manage_stock?: boolean;
  images?: File[] | ProductImage[];
  tags?: string[];
  metadata?: ProductMetadata;
  extended_metadata?: ProductExtendedMetadata;
}

export interface UpdateProductDto extends Partial<CreateProductDto> {
  id?: number;
}

export interface ProductFilters {
  search?: string;
  category?: string;
  status?: string;
  min_price?: number;
  max_price?: number;
  tags?: string[];
  sort?: 'name' | 'price' | 'created_at' | 'updated_at' | 'stock_quantity';
  order?: 'asc' | 'desc';
  page?: number;
  per_page?: number;
  view?: 'grid' | 'list';
}

export interface PaginationMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number | null;
  to: number | null;
}

export interface PaginationLinks {
  first: string;
  last: string;
  prev: string | null;
  next: string | null;
}

export interface ProductsResponse {
  data: Product[];
  meta: PaginationMeta;
  links: PaginationLinks;
}

export interface ProductResponse {
  data: Product;
  related_products?: Product[];
}

export interface ProductMutationResponse {
  data: Product;
  message: string;
}

export interface BulkDeleteResponse {
  deleted_count: number;
  message: string;
}

export interface ImageUploadResponse {
  data: Product;
  uploaded_images: ProductImage[];
  message: string;
}

// Context state for product management
export interface ProductContextState {
  products: Product[];
  selectedProduct: Product | null;
  filters: ProductFilters;
  pagination: PaginationMeta | null;
  viewMode: 'grid' | 'list';
  loading: boolean;
  error: string | null;
  selectedProductIds: number[];
}

// Form validation types
export interface ProductFormErrors {
  name?: string;
  category?: string;
  description?: string;
  status?: string;
  price?: string;
  sale_price?: string;
  currency?: string;
  stock_quantity?: string;
  manage_stock?: string;
  tags?: string;
  images?: string;
  metadata?: {
    brand?: string;
    manufacturer?: string;
    model?: string;
    weight?: string;
    dimensions?: {
      length?: string;
      width?: string;
      height?: string;
      unit?: string;
    };
  };
}

// API error response
export interface ApiErrorResponse {
  message: string;
  error?: string;
  errors?: Record<string, string[]>;
}

// Product categories (can be expanded)
export type ProductCategoryType =
  | 'electronics'
  | 'clothing'
  | 'home_garden'
  | 'books'
  | 'toys_games'
  | 'sports_outdoors'
  | 'health_beauty'
  | 'automotive'
  | 'food_beverages'
  | 'office_supplies'
  | 'other';

export const ProductCategories: Record<ProductCategoryType, string> = {
  electronics: 'エレクトロニクス',
  clothing: '衣類・ファッション',
  home_garden: 'ホーム・ガーデン',
  books: '本・書籍',
  toys_games: 'おもちゃ・ゲーム',
  sports_outdoors: 'スポーツ・アウトドア',
  health_beauty: 'ヘルス・ビューティー',
  automotive: '自動車・バイク',
  food_beverages: '食品・飲料',
  office_supplies: 'オフィス用品',
  other: 'その他',
};

// Product status options
export const ProductStatusOptions: Record<Product['status'], string> = {
  active: 'アクティブ',
  inactive: '非アクティブ',
  discontinued: '販売終了',
  fetching: '取得中',
  failed: '取得失敗',
  1: 'アクティブ',
  2: '非アクティブ',
  3: '販売終了',
  4: '取得中',
  5: '取得失敗',
};

// Currency options
export const CurrencyOptions: Record<string, string> = {
  JPY: '日本円 (¥)',
  USD: '米ドル ($)',
  EUR: 'ユーロ (€)',
  GBP: '英ポンド (£)',
};

// Sort options
export const SortOptions: Record<string, string> = {
  name: '名前',
  price: '価格',
  created_at: '登録日',
  updated_at: '更新日',
  stock_quantity: '在庫数',
};

// Default values
export const defaultProductFilters: ProductFilters = {
  search: '',
  category: '',
  status: '',
  min_price: undefined,
  max_price: undefined,
  tags: [],
  sort: 'created_at',
  order: 'desc',
  page: 1,
  per_page: 100, // Increased from 20 to show more products without pagination
  view: 'grid',
};

export const defaultProductMetadata: ProductMetadata = {
  brand: '',
  manufacturer: '',
  model: '',
  weight: '',
  dimensions: {
    length: null,
    width: null,
    height: null,
    unit: 'cm',
  },
  custom_fields: {},
};

export const defaultProductExtendedMetadata: ProductExtendedMetadata = {
  product: {
    name: '',
    brand: '',
    category: '',
    market_type: '',
    sales_model: '',
    tagline: '',
    description: '',
    usp: '',
    development_structure: '',
    use_scenarios: '',
    variations: '',
  },
  pricing_offer: {
    main_cta: '',
    price_visibility: '',
    price_details: '',
    offers_campaigns: '',
  },
  target: {
    segment: '',
    insights: {
      pain_emotion: '',
      ideal_state: '',
      barriers: '',
      purchase_factor: '',
    },
  },
  benefits: {
    functional: '',
    emotional: '',
    self_realization: '',
  },
  positioning: {
    pop: [],
    pod: [],
    trust_proof: [],
  },
  appeal: {
    recommended_types: [],
    direction: '',
    winning_copy: '',
    structure_pattern: '',
  },
};
